import { getBudgetAmountInRange, hasBudgetAmountChangeBy } from '../../utils/budgetAmounts';
import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  IconButton,
  useTheme,
  styled,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';

// Months helper
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const StickyHead = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  top: 0, // At the top of the scrollable container
  background: theme.palette.background.paper,
  zIndex: 100, // highest z-index to stay above everything
  fontWeight: 600,
  minWidth: 210, // Increased to match the cell width
  // On phones the 200px name column ate over half the viewport
  [theme.breakpoints.down('sm')]: { minWidth: 150, maxWidth: 170 },
  boxShadow: '2px 0 4px -2px rgba(0,0,0,0.12)',
  // Prevent content bleeding from left
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -20,
    top: 0,
    bottom: 0,
    width: 20,
    background: theme.palette.background.paper,
    zIndex: -1
  },
  // Override any MUI z-index
  '&.MuiTableCell-stickyHeader': {
    zIndex: '100 !important'
  }
}));

const StickyCell = styled(TableCell)(({ theme }) => ({ 
  position: 'sticky', 
  left: 0, 
  background: theme.palette.background.paper, 
  zIndex: 30, 
  fontWeight: 500, 
  whiteSpace: 'nowrap',
  minWidth: 210, // Increased to accommodate action buttons
  // On phones the 200px name column ate over half the viewport
  [theme.breakpoints.down('sm')]: { minWidth: 150, maxWidth: 170 },
  // Prevent content bleeding from left
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -20,
    top: 0,
    bottom: 0,
    width: 20,
    background: theme.palette.background.paper,
    zIndex: -1
  }
}));

// Compute the monthly allocated amount for a budget in a given year/month index
function computeMonthlyAmount(budget, year, monthIndex) {
  if (hasBudgetAmountChangeBy(budget, new Date(year, monthIndex + 1, 0))) return getBudgetAmountInRange(budget, new Date(year, monthIndex, 1), new Date(year, monthIndex + 1, 0));
  const start = new Date(budget.startDate);
  const end = budget.endDate ? new Date(budget.endDate) : new Date(2099, 11, 31);
  const monthStart = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0);
  if (end < monthStart || start > monthEnd) return 0; // no overlap

  const msPerDay = 1000 * 60 * 60 * 24;
  const overlapStart = start > monthStart ? start : monthStart;
  const overlapEnd = end < monthEnd ? end : monthEnd;
  const overlapDays = Math.max(0, Math.round((overlapEnd - overlapStart) / msPerDay) + 1);
  const daysInMonth = Math.round((monthEnd - monthStart) / msPerDay) + 1;

  if (budget.isRecurring) {
    // Non-prorated: count discrete occurrences in this month
    if (!budget.isProrated) {
      switch (budget.recurringPeriod) {
        case 'monthly':
          return budget.amount; // one occurrence per month in range
        case 'weekly': {
          // Count the number of weekday occurrences matching the startDate's weekday within overlap
            const targetDow = start.getDay();
            let count = 0;
            for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
              if (d >= start && d <= end && d.getDay() === targetDow) count++;
            }
            return count * budget.amount;
        }
        case 'quarterly': {
          // Occurs once every 3 months aligned to start month
          const startMonth = start.getMonth();
          const currentMonth = monthStart.getMonth();
          const diff = (monthStart.getFullYear() - start.getFullYear()) * 12 + (currentMonth - startMonth);
          if (diff >= 0 && diff % 3 === 0) return budget.amount / 1; // allocate full when it occurs
          return 0;
        }
        case 'yearly': {
          return start.getMonth() === monthStart.getMonth() ? budget.amount : 0;
        }
        default:
          return budget.amount;
      }
    } else {
      // Prorated: spread the amount evenly by days based on recurrence cycle length
      let cycleDays;
      switch (budget.recurringPeriod) {
        case 'weekly':
          cycleDays = 7; break;
        case 'monthly':
          // approximate monthly to average 30.4375 days or use actual month length? Use actual month length simplified to daysInMonth
          // We'll treat monthly as full month allocation so prorate by overlap fraction
          return budget.amount * (overlapDays / daysInMonth);
        case 'quarterly':
          cycleDays = 91.25; break; // approx 365/4
        case 'yearly':
          cycleDays = 365; break;
        default:
          cycleDays = daysInMonth;
      }
      if (budget.recurringPeriod === 'weekly') {
        return (budget.amount / 7) * overlapDays; // weekly amount divided by 7 per your spec
      }
      if (budget.recurringPeriod === 'quarterly') {
        return budget.amount * (overlapDays / cycleDays);
      }
      if (budget.recurringPeriod === 'yearly') {
        return budget.amount * (overlapDays / cycleDays);
      }
      return budget.amount * (overlapDays / daysInMonth);
    }
  }

  // One-time or date-range budget: prorate by overlap portion of month
  if (overlapDays <= 0) return 0;
  return Math.abs(budget.amount) * (overlapDays / daysInMonth);
}

const formatCurrency = (value) => {
  if (!value) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.abs(value));
};

const BudgetMatrixView = ({ budgets, categories, year: externalYear, onYearChange, filterType, searchTerm, getCategoryName, onEditBudget, onDeleteBudget, onAddBudget }) => {
  const theme = useTheme();
  const now = new Date();
  const initialYear = externalYear || now.getFullYear();
  const [year, setYear] = useState(initialYear);
  const [expandedSections, setExpandedSections] = useState(new Set());

  // Utility to determine readable contrast text for a background color
  const getContrastText = useCallback((hex) => {
    if (!hex) return '#000';
    const cleaned = hex.replace('#','');
    let r=0,g=0,b=0;
    if (cleaned.length === 3) {
      r = parseInt(cleaned[0]+cleaned[0],16);
      g = parseInt(cleaned[1]+cleaned[1],16);
      b = parseInt(cleaned[2]+cleaned[2],16);
    } else if (cleaned.length === 6) {
      r = parseInt(cleaned.slice(0,2),16);
      g = parseInt(cleaned.slice(2,4),16);
      b = parseInt(cleaned.slice(4,6),16);
    }
    const luminance = (0.2126*r + 0.7152*g + 0.0722*b) / 255;
    return luminance > 0.55 ? '#000' : '#fff';
  }, []);

  // Sync with external changes
  React.useEffect(() => {
    if (externalYear && externalYear !== year) setYear(externalYear);
  }, [externalYear, year]);

  const changeYear = useCallback((delta) => {
    const newYear = year + delta;
    setYear(newYear);
    onYearChange && onYearChange(newYear);
  }, [year, onYearChange]);

  const toggleSection = useCallback((sectionKey) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) newSet.delete(sectionKey); else newSet.add(sectionKey);
      return newSet;
    });
  }, []);

  // Build hierarchical rows per category with child budget rows
  const categoryRows = useMemo(() => {
    // Filter budgets first
    const filtered = budgets.filter(b => {
      const name = b.name || getCategoryName(b.categoryId) || '';
      if (searchTerm && !name.toLowerCase().includes(searchTerm.toLowerCase()) && !getCategoryName(b.categoryId).toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (filterType === 'active' && !b.isActive) return false;
      if (filterType === 'inactive' && b.isActive) return false;
      if (filterType === 'income' && b.type !== 'Income') return false;
      if (filterType === 'expense' && b.type !== 'Expense') return false;
      return true;
    });

    // Group by category
    const categoryGroups = new Map();
    filtered.forEach(budget => {
      const categoryName = getCategoryName(budget.categoryId) || 'Uncategorized';
      const categoryKey = `${categoryName}_${budget.type}`;
      const categoryObj = categories.find(cat => cat.id === budget.categoryId);
      const categoryColor = categoryObj ? (categoryObj.color || '#9e9e9e') : '#9e9e9e';
      if (!categoryGroups.has(categoryKey)) {
        categoryGroups.set(categoryKey, { categoryName, type: budget.type, color: categoryColor, budgets: [] });
      }
      categoryGroups.get(categoryKey).budgets.push(budget);
    });

    const rowsUnordered = Array.from(categoryGroups.values()).map(group => {
      const budgetRows = group.budgets.map(budget => {
        const monthly = MONTHS.map((_, idx) => computeMonthlyAmount(budget, year, idx));
        const total = monthly.reduce((s, v) => s + v, 0);
        return { budget, monthly, total };
      });
      const monthlyTotals = MONTHS.map((_, i) => budgetRows.reduce((s, r) => s + r.monthly[i], 0));
      const total = monthlyTotals.reduce((s, v) => s + v, 0);
      return { categoryName: group.categoryName, type: group.type, color: group.color, monthly: monthlyTotals, total, budgets: budgetRows };
    }).filter(r => r.budgets.length > 0);
    // Stable partition: keep relative order within each type while ensuring Income rows appear first
    const income = [];
    const expense = [];
    for (const r of rowsUnordered) {
      (r.type === 'Income' ? income : expense).push(r);
    }
    return [...income, ...expense];
  }, [budgets, categories, year, filterType, searchTerm, getCategoryName]);

  // Expand/collapse all sections
  const expandAll = useCallback(() => {
    const allSectionKeys = categoryRows.map(row => `${row.categoryName}_${row.type}`);
    setExpandedSections(new Set(allSectionKeys));
  }, [categoryRows]);

  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
  }, []);

  // Track whether all sections are expanded for the toggle button
  const allExpanded = categoryRows.length > 0 && categoryRows.every(row => expandedSections.has(`${row.categoryName}_${row.type}`));
  const toggleExpandAll = useCallback((e) => {
    e && e.stopPropagation && e.stopPropagation();
    if (allExpanded) collapseAll(); else expandAll();
  }, [allExpanded, expandAll, collapseAll]);

  // Auto-expand sections with fewer budgets for better initial UX
  React.useEffect(() => {
    const autoExpandKeys = categoryRows
      .filter(row => row.budgets.length <= 3) // Auto-expand categories with 3 or fewer budgets
      .map(row => `${row.categoryName}_${row.type}`);
    
    setExpandedSections(new Set(autoExpandKeys));
  }, [categoryRows]);

  // Compute totals across all categories
  const totalsRow = useMemo(() => {
    const incomeTotals = MONTHS.map((_, i) => categoryRows.filter(r => r.type === 'Income').reduce((s, r) => s + r.monthly[i], 0));
    const expenseTotals = MONTHS.map((_, i) => categoryRows.filter(r => r.type === 'Expense').reduce((s, r) => s + r.monthly[i], 0));
    const netTotals = MONTHS.map((_, i) => incomeTotals[i] - expenseTotals[i]);
    const incomeGrand = incomeTotals.reduce((s, v) => s + v, 0);
    const expenseGrand = expenseTotals.reduce((s, v) => s + v, 0);
    const netGrand = netTotals.reduce((s, v) => s + v, 0);
    return { incomeTotals, expenseTotals, netTotals, incomeGrand, expenseGrand, netGrand };
  }, [categoryRows]);

  return (
    <Paper sx={{ borderRadius: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ 
        display:'flex', 
        justifyContent:'center', 
        alignItems:'center', 
        p: 1.5,
        pb: 1,
        background: (theme) => theme.palette.background.paper,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0 // Prevent shrinking
      }}>
        {/* Center: Year navigator */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
          <IconButton
            size="small"
            onClick={() => changeYear(-1)}
            sx={{ p: 0.6, bgcolor: 'common.white', boxShadow: '0 2px 4px rgba(0,0,0,0.04)', borderRadius: 3, width: 40, height: 40, border: '1px solid', borderColor: 'grey.300', '&:hover': { bgcolor: 'grey.50' } }}
          >
            <KeyboardArrowLeftIcon sx={{ fontSize: '0.875rem', color: 'text.primary' }} />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary', textAlign: 'center', minWidth: 92, letterSpacing: 0.6, textTransform: 'uppercase' }}>
            {year}
          </Typography>

          <IconButton
            size="small"
            onClick={() => changeYear(1)}
            sx={{ p: 0.6, bgcolor: 'common.white', boxShadow: '0 2px 4px rgba(0,0,0,0.04)', borderRadius: 3, width: 40, height: 40, border: '1px solid', borderColor: 'grey.300', '&:hover': { bgcolor: 'grey.50' } }}
          >
            <KeyboardArrowRightIcon sx={{ fontSize: '0.875rem', color: 'text.primary' }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, pt: 0 }}>
        {/* Phones: natural table width so the narrowed sticky column doesn't
            leave a stretched empty gutter; desktop keeps the 1160 floor. */}
        <Box sx={{ minWidth: { xs: 0, sm: 1200 }, width: { xs: 'max-content', sm: 'auto' } }}>
        <Table size="small" stickyHeader sx={{
          '& th, & td': { borderRight:'1px solid', borderColor:'divider' },
          '& th:last-of-type, & td:last-of-type': { borderRight:'none' },
          '& thead th': {
            position: 'sticky',
            top: 0, // At the top of the scrollable container
            background: (theme)=>theme.palette.background.paper,
            zIndex: 5
          }
        }}>
          <TableHead>
            <TableRow>
                <StickyHead sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); toggleExpandAll(e); }}
                    aria-pressed={allExpanded}
                    aria-label={allExpanded ? 'Collapse all categories' : 'Expand all categories'}
                    sx={{
                      p: 0.25,
                      color: allExpanded ? 'primary.contrastText' : 'inherit',
                      transform: allExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      bgcolor: 'rgba(0,0,0,0.06)',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' }
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                  Category
                </StickyHead>
              {MONTHS.map(m=> <TableCell key={m} align="center" sx={{ fontSize:'0.8125rem', fontWeight:600 }}>{m}</TableCell>)}
              <TableCell align="right" sx={{ fontSize:'0.8125rem', fontWeight:600 }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categoryRows.map(row => {
              const sectionKey = `${row.categoryName}_${row.type}`;
              const isExpanded = expandedSections.has(sectionKey);
              const contrast = '#fff'; // Force white text per request
              return (
                <React.Fragment key={sectionKey}>
                  <TableRow
                    hover
                    onClick={() => toggleSection(sectionKey)}
                  >
                    <StickyCell sx={{
                      fontWeight:500,
                      display:'flex',
                      alignItems:'center',
                      gap:1,
                      backgroundColor: row.color,
                      color: '#fff',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -20,
                        top: 0,
                        bottom: 0,
                        width: 20,
                        // Keep the sticky bleed area neutral so only the cell rectangle is colored
                        backgroundColor: (theme)=> theme.palette.background.paper,
                        zIndex: -1
                      },
                      transition: 'filter 0.2s',
                      cursor: 'pointer',
                      '&:hover': { filter: 'brightness(0.95)' }
                    }}>
                      <IconButton
                        size="small"
                        sx={{
                          p: 0.25,
                          transition: 'transform 0.2s, background-color 0.12s',
                          transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                          color: 'inherit',
                          bgcolor: 'rgba(255,255,255,0.14)',
                          borderRadius: '50%',
                          width: 28,
                          height: 28,
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' }
                        }}
                        onClick={(e) => { e.stopPropagation(); toggleSection(sectionKey); }}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? `Collapse ${row.categoryName}` : `Expand ${row.categoryName}`}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                      <Box sx={{ display:'flex', flexDirection:'column', flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight:500, color:'#fff', lineHeight:1.1, fontSize: '0.875rem' }} noWrap title={row.categoryName}>
                          {row.categoryName}
                        </Typography>
                        <Typography variant="caption" sx={{ lineHeight:1.1, color:'rgba(255,255,255,0.85)', fontSize:'0.55rem', letterSpacing:0.25 }}>
                          {row.type} • {row.budgets.length} budget{row.budgets.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      {onAddBudget && (
                        <Tooltip title={`Add ${row.type} budget for ${row.categoryName}`}>
                          <IconButton 
                            size="small" 
                            sx={{ 
                              p: 0.25, 
                              color: 'inherit',
                              opacity: 0.8,
                              '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.1)' }
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent expanding/collapsing the section
                              const categoryId = categories.find(cat => getCategoryName(cat.id) === row.categoryName)?.id;
                              onAddBudget({ 
                                categoryId,
                                type: row.type,
                                categoryName: row.categoryName 
                              });
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </StickyCell>
                    {row.monthly.map((amt,i)=> (
                      <TableCell key={i} align="right" sx={{
                        fontSize:'0.75rem',
                        fontWeight:700,
                        color: row.type === 'Income' ? 'success.main':'error.main',
                        background: (theme) => row.type === 'Income' ? alpha(theme.palette.success.main, 0.03) : alpha(theme.palette.error.main, 0.03)
                      }}>
                        {formatCurrency(amt)}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{
                      fontSize:'0.75rem',
                      fontWeight:700,
                      color: row.type === 'Income' ? 'success.main':'error.main',
                      background: (theme) => row.type === 'Income' ? alpha(theme.palette.success.main, 0.04) : alpha(theme.palette.error.main, 0.04)
                    }}>
                      {formatCurrency(row.total)}
                    </TableCell>
                  </TableRow>
                  {isExpanded && row.budgets.map(budgetRow => {
                    return (
                      <TableRow key={budgetRow.budget.id}>
                        <StickyCell sx={{ pl:3, fontSize:'0.7rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <Box
                            component="span"
                            sx={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                            title={budgetRow.budget.name || 'Unnamed Budget'}
                          >
                            {budgetRow.budget.name || 'Unnamed Budget'}
                          </Box>
                          <Box sx={{ display:'flex', gap:0.5, ml:1, flexShrink:0 }}>
                            <Tooltip title="Edit Budget">
                              <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); onEditBudget && onEditBudget(budgetRow.budget); }} sx={{ p:0.25, '&:hover': { backgroundColor:'primary.light', color:'primary.contrastText' } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Budget">
                              <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); onDeleteBudget && onDeleteBudget(budgetRow.budget); }} sx={{ p:0.25, color:'error.main', '&:hover': { backgroundColor:'error.light', color:'error.contrastText' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </StickyCell>
                        {budgetRow.monthly.map((amt,i)=> (
                          <TableCell key={i} align="right" sx={{ fontSize:'0.8125rem', fontWeight:600, color: budgetRow.budget.type === 'Income' ? 'success.main':'error.main' }}>
                            {amt ? formatCurrency(amt) : '—'}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontSize:'0.8125rem', fontWeight:700, color: budgetRow.budget.type === 'Income' ? 'success.main':'error.main' }}>
                          {budgetRow.total ? formatCurrency(budgetRow.total) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {categoryRows.length > 0 && (
              <>
                {/* Income Totals */}
                <TableRow>
                  <StickyCell sx={{ fontWeight: 600, background: (theme) => alpha(theme.palette.success.main, 0.06) }}>Income Total</StickyCell>
                  {totalsRow.incomeTotals.map((amt, idx) => (
                    <TableCell key={idx} align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: 'success.main', background: (theme) => alpha(theme.palette.success.main, 0.04) }}>
                      {formatCurrency(amt)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: 'success.main', background: (theme) => alpha(theme.palette.success.main, 0.04) }}>{formatCurrency(totalsRow.incomeGrand)}</TableCell>
                </TableRow>
                {/* Expense Totals */}
                <TableRow>
                  <StickyCell sx={{ fontWeight: 600, background: (theme) => alpha(theme.palette.error.main, 0.06) }}>Expense Total</StickyCell>
                  {totalsRow.expenseTotals.map((amt, idx) => (
                    <TableCell key={idx} align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: 'error.main', background: (theme) => alpha(theme.palette.error.main, 0.04) }}>
                      {formatCurrency(amt)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: 'error.main', background: (theme) => alpha(theme.palette.error.main, 0.04) }}>{formatCurrency(totalsRow.expenseGrand)}</TableCell>
                </TableRow>
                {/* Net Row */}
                <TableRow>
                  <StickyCell sx={{ fontWeight: 700, background: (theme) => alpha(theme.palette.text.primary, 0.04) }}>Net (Income - Expenses)</StickyCell>
                  {totalsRow.netTotals.map((amt, idx) => (
                    <TableCell key={idx} align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: amt >= 0 ? 'success.main' : 'error.main', background: (theme) => alpha(theme.palette.text.primary, 0.03) }}>
                      {formatCurrency(amt)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: totalsRow.netGrand >= 0 ? 'success.main' : 'error.main', background: (theme) => alpha(theme.palette.text.primary, 0.03) }}>{formatCurrency(totalsRow.netGrand)}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
        </Box>
      </Box>
    </Paper>
  );
};

export default BudgetMatrixView;

